/**
 * Price helpers that read selections from mock attributes (single source of truth)
 * and prices directly from products.json.
 *
 * This completely bypasses the Redux `attributes.price` / `activeItemId` fields
 * which get wiped by various SET_ATTRIBUTES dispatches during the app lifecycle.
 */

import { getMockAttributes } from './threekitMocks';
import priceData from '../../products.json';

const QUANTITY_MAP: Record<string, number> = {
  Tire: 4,
  Wheels: 4,
  Mirror: 2,
  'Hood Catcher': 2,
};

export interface AttributePriceInfo {
  name: string;
  selectedLabel: string;
  priceId: string | undefined;
  unitPrice: number;
  quantity: number;
  subtotal: number;
}

/**
 * Compute per-attribute price info by reading current selections from mock data
 * and looking up prices in products.json.
 *
 * @param reduxAttributes - Redux `actualAttributes` (used only for user-modified quantities)
 */
export function getAttributePrices(reduxAttributes?: any): AttributePriceInfo[] {
  const mocks = getMockAttributes();
  const result: AttributePriceInfo[] = [];

  for (const [name, attr] of Object.entries(mocks) as [string, any][]) {
    if (!attr || !attr.values || attr.values.length === 0) continue;

    // Find currently selected variant from mock data (source of truth)
    const isAsset = attr.type === 'Asset';
    const currentValue = isAsset ? attr.value?.assetId : attr.value;
    const selectedVariant = attr.values.find((v: any) =>
      isAsset ? v.assetId === currentValue : v.value === currentValue
    );

    if (!selectedVariant || selectedVariant.label === 'None') {
      result.push({
        name,
        selectedLabel: selectedVariant?.label ?? 'None',
        priceId: undefined,
        unitPrice: 0,
        quantity: 1,
        subtotal: 0,
      });
      continue;
    }

    const priceId = selectedVariant.metadata?._id;
    const priceEntry = priceId ? (priceData as any)[priceId] : null;
    const unitPrice = priceEntry?.price ?? 0;

    // Quantity: prefer user-modified value from Redux, fall back to defaults
    const reduxQty = reduxAttributes?.[name]?.shopInfo?.quantity;
    const quantity = reduxQty ?? QUANTITY_MAP[name] ?? 1;

    result.push({
      name,
      selectedLabel: selectedVariant.label,
      priceId,
      unitPrice,
      quantity,
      subtotal: unitPrice * quantity,
    });
  }

  return result;
}

/**
 * Compute total price across all attributes.
 */
export function getTotalPrice(reduxAttributes?: any): number {
  const prices = getAttributePrices(reduxAttributes);
  const total = prices.reduce((sum, p) => sum + p.subtotal, 0);
  return Number(total.toFixed(2));
}

/**
 * Look up the unit price for a given priceId from products.json.
 */
export function getPriceById(priceId: string | undefined): number {
  if (!priceId) return 0;
  return (priceData as any)[priceId]?.price ?? 0;
}

/**
 * Find the priceId for the currently selected variant of a given attribute.
 */
export function getSelectedPriceId(attributeName: string): string | undefined {
  const mocks = getMockAttributes();
  const attr = mocks[attributeName];
  if (!attr || !attr.values) return undefined;

  const isAsset = attr.type === 'Asset';
  const currentValue = isAsset ? attr.value?.assetId : attr.value;
  const selected = attr.values.find((v: any) =>
    isAsset ? v.assetId === currentValue : v.value === currentValue
  );

  if (!selected || selected.label === 'None') return undefined;
  return selected.metadata?._id;
}
